import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import * as ArchiveActions from 'archive/ArchiveActions';
import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import * as BenchlingUtil from 'BenchlingUtil';
import {setWindowTitle} from 'BenchlingUtil';
import {TagSchemaItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import navigateToURL from 'lib/navigateToURL';
import notifications from 'notifications';
import * as RegistryUtil from 'registry/RegistryUtil';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {schemaFieldsetName} from 'schemas/schema-interface/util/schemaFieldsetName';
import SchemasListing, {RowPermissions, SchemasListingSchema} from 'schemas/SchemasListing';
import SchemaInterfaceSelector from 'schemas/shared/SchemaInterfaceSelector';
import {SCHEMA_TYPES} from 'schemas/spec/SchemaConfigConstants';
import {getItemTypeDisplayName} from 'schemas/TagSchemaItemTypeUtil';
import {
  RegistryTagSchemasSettingsPageQuery,
  RegistryTagSchemasSettingsPageQueryVariables,
} from 'settings/registry/__generated/RegistryTagSchemasSettingsPageQuery';
import {
  RegistryTagSchemasSettingsPageWithAddedInfoQuery,
  RegistryTagSchemasSettingsPageWithAddedInfoQueryVariables,
} from 'settings/registry/__generated/RegistryTagSchemasSettingsPageWithAddedInfoQuery';
import getRegistrySchemasTitle from 'settings/registry/getRegistrySchemasTitle';
import TagSchemaItemTypeSelector from 'settings/registry/TagSchemaItemTypeSelector';
import TagSchemaValidationStatusFilter, {
  ValidationStatusFilter,
} from 'settings/registry/TagSchemaValidationStatusFilter';
import {ValidationStatusSummary} from 'settings/registry/validation/ValidationSummaries';
import * as TagSchemaActions from 'tag-schema/TagSchemaActions';
import * as Colors from 'ui-library/Colors';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {capwords} from 'util/NameUtil';
import sortedBy from 'util/sortedBy';

export type TagSchemaListing = SchemasListingSchema & {
  iconId: BenchlingIconName;
  itemType: TagSchemaItemType;
};
export type TagSchemaListingWithAddedInfo = TagSchemaListing & {
  permissions: Permissions;
  fieldsJson: Array<SchemaField>;
  validationStatusSummary: ValidationStatusSummary | null;
  interfaces: Array<{id: string; name: string}>;
};

function hasAddedInfo(
  listing: TagSchemaListing | TagSchemaListingWithAddedInfo
): listing is TagSchemaListingWithAddedInfo {
  return (
    listing.hasOwnProperty('permissions') &&
    listing.hasOwnProperty('fieldsJson') &&
    listing.hasOwnProperty('validationStatusSummary')
  );
}

export const REGISTRY_TAG_SCHEMAS_SETTINGS_PAGE_QUERY = gql`
  query RegistryTagSchemasSettingsPageQuery($registryId: StrictString!)
  @skipAudit
  @engComponent(name: REGISTRY) {
    registry(id: $registryId) {
      ... on RegistryNode {
        id
        tagSchemas {
          id
          iconId
          itemType
          name
          ...HasArchiveRecordGraphQL
        }
        ...NavStoreRegistryGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('HasArchiveRecord').fragment}
  ${getFragmentWrapper('NavStoreRegistry').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const REGISTRY_TAG_SCHEMAS_SETTINGS_PAGE_WITH_ADDED_INFO_QUERY = gql`
  query RegistryTagSchemasSettingsPageWithAddedInfoQuery($registryId: StrictString!)
  @skipAudit
  @engComponent(name: REGISTRY) {
    registry(id: $registryId) {
      ... on RegistryNode {
        id
        tagSchemas {
          id
          permissions {
            ...SchemaPermissionsGraphQL
          }
          fieldsJson
          iconId
          itemType
          name
          interfaces {
            id
            name
          }
          ...HasArchiveRecordGraphQL
          ...TagSchemaValidationStatusSummaryGraphQL
        }
        ...NavStoreRegistryGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('HasArchiveRecord').fragment}
  ${getFragmentWrapper('SchemaPermissions').fragment}
  ${getFragmentWrapper('TagSchemaValidationStatusSummary').fragment}
  ${getFragmentWrapper('NavStoreRegistry').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const STATUS_SUMMARY_QUERY_POLL_INTERVAL_MS = 120_000;

interface Props {
  registryId: string;
  ownerUrlId: string;
}
interface QueryingProps extends Props {
  isLoading: boolean;
  isWritable: boolean;
  listings: Array<TagSchemaListing | TagSchemaListingWithAddedInfo> | null;
  refetch: () => void;
}

function getSchemaDescriptor(listing: TagSchemaListing): Array<JSX.Element | string> {
  return [
    <div>
      <BenchlingIcon className="typeIcon u-small-spacing-left" icon={listing.iconId} />
      <span className="u-small-spacing-left">
        {listing.name}
        <ArchiveRecordLabel item={listing} />
      </span>
    </div>,
    getItemTypeDisplayName(listing.itemType),
  ];
}

function mapSchemaWithoutAddedInfoToRow(listing: TagSchemaListing): Array<JSX.Element | string> {
  return [...getSchemaDescriptor(listing), <HorizontalThrobber />];
}

export function RegistryTagSchemasSettingsPage(props: QueryingProps): JSX.Element {
  const [validationStatusFilter, setValidationStatusFilter] = useState(ValidationStatusFilter.ALL);
  const [selectedItemType, setSelectedItemType] = useState<TagSchemaItemType | null>(null);
  const [selectedInterface, setSelectedInterface] = useState<string | null>(null);

  const createHandler = (): void => {
    BenchlingUtil.navigate(null, RegistryUtil.getCreateTagSchemaUrl(props.ownerUrlId, props.registryId));
  };

  const urlForSchema = (schemaId: string): string =>
    RegistryUtil.getTagSchemaSettingsUrl(props.ownerUrlId, props.registryId, schemaId);

  function renderValidationSummaryColumns(tagSchema: TagSchemaListingWithAddedInfo): Array<JSX.Element> {
    if (!tagSchema.validationStatusSummary || tagSchema.validationStatusSummary.numFailed === 0) {
      return [<span />];
    }

    const schemaValidationSectionUrl = `${RegistryUtil.getTagSchemaSettingsUrl(
      props.ownerUrlId,
      props.registryId,
      tagSchema.id
    )}/validation`;

    return [
      <div
        onClick={(event) => {
          event.stopPropagation();
          event.preventDefault();
          navigateToURL(schemaValidationSectionUrl);
        }}
        className={css(styles.validationSummaryLink)}
      >
        {tagSchema.validationStatusSummary.numFailed}
      </div>,
    ];
  }

  function mapSchemaToRowValue(
    listing: TagSchemaListing | TagSchemaListingWithAddedInfo
  ): Array<JSX.Element | string> {
    if (!hasAddedInfo(listing)) {
      return mapSchemaWithoutAddedInfoToRow(listing);
    }

    return [
      ...getSchemaDescriptor(listing),
      ...(BenchlingConfig.SCHEMAS_ENABLE_INTERFACES
        ? [listing.interfaces.map(({name}) => name).join(', ')]
        : []),
      listing.fieldsJson.map(({displayName}) => displayName).join(', '),
      ...renderValidationSummaryColumns(listing),
    ];
  }

  function mapSchemaToRowPermissions(
    listing: TagSchemaListing | TagSchemaListingWithAddedInfo
  ): RowPermissions {
    if (!hasAddedInfo(listing)) {
      return {isArchivable: false, isDeletable: false};
    }

    return {
      isArchivable: Boolean(listing.permissions.archivable),
      isDeletable: Boolean(listing.permissions.definitionDeletable),
    };
  }

  useEffect(() => {
    setWindowTitle(`${getRegistrySchemasTitle()} - Registry Settings`);
  }, []);

  const columnHeaders = [
    {name: 'Schemas', width: BenchlingConfig.SCHEMAS_ENABLE_INTERFACES ? 3 : 4},
    {name: 'Entity Type', width: 2},
    ...(BenchlingConfig.SCHEMAS_ENABLE_INTERFACES ? [{name: capwords(schemaFieldsetName()), width: 2}] : []),
    {name: 'Entity Fields', width: BenchlingConfig.SCHEMAS_ENABLE_INTERFACES ? 4 : 5},
    {name: 'Validation Failures', width: 1},
  ];

  function filterSchemasByInterfaceId(schemas: Array<TagSchemaListing>): Array<TagSchemaListing> {
    if (selectedInterface == null) {
      return schemas;
    }

    return schemas.filter((schema) => {
      if (!hasAddedInfo(schema)) {
        return true;
      }

      return schema.interfaces.some((interfaceOption) => interfaceOption.id === selectedInterface);
    });
  }

  function filterSchemasByValidationStatus(schemas: Array<TagSchemaListing>): Array<TagSchemaListing> {
    if (validationStatusFilter === ValidationStatusFilter.ALL) {
      return schemas;
    }

    return schemas.filter(
      (schema) =>
        (validationStatusFilter === ValidationStatusFilter.WITH_ERRORS &&
          hasAddedInfo(schema) &&
          schema.validationStatusSummary != null &&
          schema.validationStatusSummary.numFailed > 0) ||
        (validationStatusFilter === ValidationStatusFilter.WITHOUT_ERRORS &&
          (!hasAddedInfo(schema) ||
            schema.validationStatusSummary == null ||
            schema.validationStatusSummary.numFailed === 0))
    );
  }

  function filterSchemasByEntityType(schemas: Array<TagSchemaListing>): Array<TagSchemaListing> {
    if (selectedItemType == null) {
      return schemas;
    }

    return schemas.filter((schema) => schema.itemType === selectedItemType);
  }

  function customFilterCallback(schemas: Array<TagSchemaListing>): Array<TagSchemaListing> {
    let filteredSchemas = [...schemas];

    if (!BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE) {
      // Hide molecule schemas if chemistry is not enabled
      filteredSchemas = filteredSchemas.filter((schema) => schema.itemType !== TagSchemaItemType.MOLECULE);
    }

    filteredSchemas = filterSchemasByValidationStatus(filteredSchemas);
    filteredSchemas = filterSchemasByEntityType(filteredSchemas);
    filteredSchemas = filterSchemasByInterfaceId(filteredSchemas);

    return filteredSchemas;
  }

  return (
    <SchemasListing
      columnHeaders={columnHeaders}
      customSearchControls={
        <>
          <div className={css(styles.filterContainer)}>
            <TagSchemaItemTypeSelector
              displayName={selectedItemType ? getItemTypeDisplayName(selectedItemType) : 'All'}
              entityOptionConfiguration={{
                defaultOptionName: 'All',
              }}
              selectedItemType={selectedItemType}
              showLabel={true}
              onChangeItemType={setSelectedItemType}
              width={Menu.WIDTHS.MEDIUM}
            />
          </div>
          <div className={css(styles.filterContainer)}>
            <TagSchemaValidationStatusFilter
              selectedValidationStatus={validationStatusFilter}
              onChangeValidationStatusFilter={setValidationStatusFilter}
            />
          </div>
          {BenchlingConfig.SCHEMAS_ENABLE_INTERFACES ? (
            <div className={css(styles.filterContainer)}>
              <SchemaInterfaceSelector
                selectedInterfaceId={selectedInterface}
                onChangeSelectedInterfaceId={setSelectedInterface}
              />
            </div>
          ) : null}
        </>
      }
      customFilterCallback={customFilterCallback}
      schemas={props.listings}
      schemaType={SCHEMA_TYPES.ENTITY}
      navigateToCreate={createHandler}
      urlForSchema={urlForSchema}
      archiveSchemas={async (itemIds, purpose) => {
        await ArchiveActions.bulkArchiveTagSchemas(itemIds, purpose);
        props.refetch();
      }}
      unarchiveSchemas={async (ids) => {
        await ArchiveActions.bulkUnarchiveTagSchemas(ids);
        props.refetch();
      }}
      deleteSchema={(schemaId) => {
        TagSchemaActions.delete_(schemaId);
        props.refetch();
      }}
      isWritable={props.isWritable}
      mapSchemaToRowValue={mapSchemaToRowValue}
      mapSchemaToRowPermissions={mapSchemaToRowPermissions}
    />
  );
}

function QueryingRegistryTagSchemasSettingsPage(props: Props): JSX.Element {
  const tagSchemasQuery = useBenchlingQuery<
    RegistryTagSchemasSettingsPageQuery,
    RegistryTagSchemasSettingsPageQueryVariables
  >(REGISTRY_TAG_SCHEMAS_SETTINGS_PAGE_QUERY, {variables: {registryId: props.registryId}});
  const loadedPage = tagSchemasQuery.type === QueryResultType.SUCCESS;
  const registryWithoutAddedInfo = loadedPage ? tagSchemasQuery.data.registry : null;
  const tagSchemasWithAddedInfoQuery = useBenchlingQuery<
    RegistryTagSchemasSettingsPageWithAddedInfoQuery,
    RegistryTagSchemasSettingsPageWithAddedInfoQueryVariables
  >(REGISTRY_TAG_SCHEMAS_SETTINGS_PAGE_WITH_ADDED_INFO_QUERY, {
    variables: {registryId: props.registryId},
    fetchPolicy: 'cache-and-network',
    pollInterval: STATUS_SUMMARY_QUERY_POLL_INTERVAL_MS,
    renderCachedDataWhenRefetching: true,
  });

  const refetch = (): void => {
    tagSchemasQuery.refetch();
    tagSchemasWithAddedInfoQuery.refetch();
  };

  const loadedAddedInfo = tagSchemasWithAddedInfoQuery.type === QueryResultType.SUCCESS;
  const registry = loadedAddedInfo ? tagSchemasWithAddedInfoQuery.data.registry : registryWithoutAddedInfo;
  let isWritable = false;
  let listings = null;
  if (registry) {
    if (isInaccessible(registry)) {
      setWindowTitle(`${getRegistrySchemasTitle()} - Registry Settings`);
      notifications.error('You do not have permission to access this registry', {stack: true});
      return <></>;
    }
    isWritable = RegistryUtil.hasSettingsAccess(graphQLToREST('NavStoreRegistry', registry));
    listings = sortedBy(
      registry.tagSchemas as Array<TagSchemaListing> | Array<TagSchemaListingWithAddedInfo>,
      [
        {
          accessor: ({name}) => name,
          naturalSort: true,
        },
      ]
    );
  }

  return (
    <RegistryTagSchemasSettingsPage
      {...props}
      key={props.registryId}
      isLoading={!loadedAddedInfo && !loadedPage}
      isWritable={isWritable}
      listings={listings}
      refetch={refetch}
    />
  );
}

const styles = StyleSheet.create({
  validationSummaryLink: {
    display: 'inline-block',
    height: '100%',
    width: '75%',
    float: 'left',
    textAlign: 'right',
    color: Colors.BLUE_LIGHT,
    padding: '17px 0 14px',
    margin: '-17px 0 -14px',

    ':hover': {
      color: Colors.BLUE,
      textDecoration: 'underline',
    },
  },
  filterContainer: {
    'min-width': '100px',
  },
});

export default QueryingRegistryTagSchemasSettingsPage;
